import {ElementRef, Inject, inject, Injectable, InjectionToken, NgZone, OnDestroy, Optional} from "@angular/core";
import {WINDOW_REF_TOKEN} from "../../tokens";
import {Subject} from "rxjs";

export const RESIZE_OBSERVER_AVAILABLE = new InjectionToken<boolean>('RESIZE_OBSERVER_AVAILABLE', {
  providedIn: 'root',
  factory() {
    const windowRef = inject(WINDOW_REF_TOKEN);
    return Boolean((windowRef as any).ResizeObserver);
  }
});

type TROSizes = {
  width: number;
  height: number;
}

@Injectable()
export class IntResizeObserverService implements OnDestroy {

  resizeObserverRef!: ResizeObserver;

  observable$: Subject<ResizeObserverEntry[]> = new Subject<ResizeObserverEntry[]>();

  rafRef!: null | number;

  constructor(
    @Inject(ElementRef) private readonly elementRef: ElementRef,
    @Inject(NgZone) private readonly ngZone: NgZone,
    @Optional()
    @Inject(RESIZE_OBSERVER_AVAILABLE) private readonly isResizeObserverAvailable: boolean,
    @Inject(WINDOW_REF_TOKEN) private readonly windowRef: Window
  ) {
    if(!this.isResizeObserverAvailable) {
      console.warn('Resize observer is not available');
    } else {
      this.resizeObserverRef = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        const elements = entries.map(e => e.target);
        this.check(elements, this.resizeObserverRef, () => {
          this.ngZone.run(() => {
            this.observable$.next(entries);
          });
        });
      });
      this.resizeObserverRef.observe(this.elementRef.nativeElement);
    }
  }

  private check(elementsRef: Array<Element>, currentObserver: ResizeObserver, cb: () => void) {
    if(this.rafRef !== null) {
      this.windowRef.cancelAnimationFrame(this.rafRef);
    }
    const initial = elementsRef.map(e => e.getBoundingClientRect());
    cb();
    const afterCheck = elementsRef.map(e => e.getBoundingClientRect());
    const resizedElements = elementsRef.filter((_, i) => {
      return initial[i].width !== afterCheck[i].width || initial[i].height !== afterCheck[i].height
    });
    resizedElements.forEach((e) => currentObserver.unobserve(e));
    this.rafRef = this.windowRef.requestAnimationFrame(() => {
      resizedElements.forEach(e => currentObserver.observe(e));
    })
  }

  ngOnDestroy() {
    if(this.resizeObserverRef) {
      this.resizeObserverRef.disconnect();
    }
    this.observable$.complete();
  }
}
